import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { getVenues } from "@/api/endpoints";
import { useI18n } from "@/i18n";
import { StatCard } from "@/components/stat-card";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Store,
  UtensilsCrossed,
  QrCode,
  Plus,
  ArrowRight,
} from "lucide-react";

export function DashboardPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { data: venues, isLoading } = useQuery({
    queryKey: ["venues"],
    queryFn: getVenues,
  });

  if (isLoading) {
    return <div className="py-8 text-center text-muted-foreground">{t("loading")}</div>;
  }

  if (!venues || venues.length === 0) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center">
        <div className="flex size-16 items-center justify-center rounded-2xl bg-primary/10 mb-4">
          <Store className="size-8 text-primary" />
        </div>
        <h2 className="text-xl font-bold mb-2">{t("welcome")}</h2>
        <p className="text-muted-foreground mb-6 text-center max-w-md">
          {t("create_first_venue")}
        </p>
        <Button onClick={() => navigate("/venues")} size="lg">
          <Plus className="mr-2 size-5" />
          {t("create_venue")}
        </Button>
      </div>
    );
  }

  const totalMenus = venues.reduce((sum, v) => sum + (v._count?.menus ?? 0), 0);
  const totalQr = venues.reduce((sum, v) => sum + (v._count?.qrCodes ?? 0), 0);

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold">{t("dashboard")}</h1>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-8">
        <StatCard title={t("venues")} value={venues.length} icon={Store} />
        <StatCard title={t("total_menus")} value={totalMenus} icon={UtensilsCrossed} />
        <StatCard title={t("qr_codes")} value={totalQr} icon={QrCode} />
      </div>

      <h2 className="mb-4 text-lg font-semibold">{t("your_venues")}</h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {venues.map((venue) => (
          <Card
            key={venue.id}
            className="cursor-pointer transition-shadow hover:shadow-md"
            onClick={() => navigate(`/venues/${venue.id}/menu`)}
          >
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">{venue.name}</CardTitle>
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                  {venue.planType}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">menu.heremenu.app/{venue.slug}</p>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>{venue._count?.menus ?? 0} menu</span>
                <ArrowRight className="size-4" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
